import { BadgeWithTooltip } from 'components/WithTooltip/BadgeWithTooltip';
import type {
    WorkOrderStatusEnum,
    ContractStatusEnum,
    ProjectStatusEnum,
    BimWorkOrderPriceData,
    BimWorkOrderTotalItemData,
} from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import type { AnyObject } from 'types/AnyObject';
import { Button, IconExpand, Loader, IconWarning } from 'uikit';
import { formatNumberToRu } from 'utils';

import type { ColumnFunctionCallbackParams, ICellRendererParams, RowClassRules } from 'ag-grid-community';
import * as R from 'ramda';
import type React from 'react';

import { BimWorkOrderPriceTableContextMenu } from '../../WorkOrder/BimWorkOrderPriceTable/components/BimWorkOrderPriceTableContextMenu';
import { BimWorkOrderRowDetailsType, BimWorkOrderRowTypeEnum } from '../../WorkOrder/types';

export interface IBimWorkOrderRowDetails<T> {
    rowType: BimWorkOrderRowTypeEnum;
    data: T;
    detailsType: BimWorkOrderRowDetailsType;
}

export const bimNumberFormatter =
    (decimal: number) =>
    ({ value }: { value: number }) =>
        formatNumberToRu(value, decimal);

export const BimWorkOrderMenuRenderer =
    <T extends boolean>({
        selectRowAndOpenDetailsModal,
        checkRowHandler,
        checkedRows,
        isBottomTotalRow,
        makeRefreshTable,
        hideCheckBoxAndMenu,
        workOrderStatus,
        contractStatus,
        statusProject,
        orderRules,
    }: {
        isBottomTotalRow: T;
        selectRowAndOpenDetailsModal: (
            rowData: IBimWorkOrderRowDetails<T extends true ? BimWorkOrderTotalItemData : BimWorkOrderPriceData>,
        ) => void;
        checkRowHandler: ({ id, data }: { id: number; data: BimWorkOrderPriceData | null }) => void;
        checkedRows: { [key: number]: BimWorkOrderPriceData | null };
        makeRefreshTable: () => void;
        hideCheckBoxAndMenu?: boolean;
        workOrderStatus?: WorkOrderStatusEnum;
        contractStatus?: ContractStatusEnum;
        statusProject?: ProjectStatusEnum;
        orderRules?: IUseAccess['orderBim'];
    }) =>
    // eslint-disable-next-line react/display-name
    ({ data }: ColumnFunctionCallbackParams<T extends true ? BimWorkOrderTotalItemData : BimWorkOrderPriceData>) => {
        if (!data) {
            return <Loader />;
        }

        const openDetails = (detailsType?: BimWorkOrderRowDetailsType) => {
            if (isBottomTotalRow) {
                selectRowAndOpenDetailsModal?.({
                    rowType: BimWorkOrderRowTypeEnum.TotalsString,
                    data,
                    detailsType: BimWorkOrderRowDetailsType.Details,
                });
            } else if (
                detailsType &&
                [BimWorkOrderRowDetailsType.Revision, BimWorkOrderRowDetailsType.Update].includes(detailsType)
            ) {
                selectRowAndOpenDetailsModal?.({
                    rowType: BimWorkOrderRowTypeEnum.RegularString,
                    detailsType,
                    data,
                });
            } else {
                selectRowAndOpenDetailsModal?.({
                    rowType: BimWorkOrderRowTypeEnum.RegularString,
                    data,
                    detailsType: BimWorkOrderRowDetailsType.Details,
                });
            }
        };

        const openActs = () => {
            selectRowAndOpenDetailsModal?.({
                rowType: BimWorkOrderRowTypeEnum.RegularString,
                data,
                detailsType: BimWorkOrderRowDetailsType.ActsKc2,
            });
        };

        const hideExpander = hideCheckBoxAndMenu && (data as any).topPinnedRow;

        return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                {!hideCheckBoxAndMenu && !isBottomTotalRow && (
                    <BimWorkOrderPriceTableContextMenu
                        data={data as BimWorkOrderPriceData}
                        makeRefreshTable={makeRefreshTable}
                        openDetails={openDetails}
                        openActs={openActs}
                        workOrderStatus={workOrderStatus}
                        contractStatus={contractStatus}
                        orderRules={orderRules}
                        statusProject={statusProject}
                    />
                )}
                {!hideExpander && !(data as any).topPinnedRow && (
                    <Button onClick={openDetails as any} iconLeft={IconExpand} onlyIcon view="clear" size="xs" />
                )}
            </div>
        );
    };

export const cellWithDifferentFiled =
    (field: string, dotNotationField: string, leftSideGetter?: (data: any) => React.ReactNode | undefined) =>
    // eslint-disable-next-line react/display-name
    (p: ICellRendererParams) => {
        const { data, formatValue } = p;
        const LeftSide = leftSideGetter?.(data);

        if (data === undefined) {
            return LeftSide || '';
        }

        if (data?.[R.split('.', dotNotationField)[0]]?.hasErrors) {
            return (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <BadgeWithTooltip
                        messages={data?.[R.split('.', dotNotationField)[0]]?.errors}
                        size="l"
                        badgeSize="s"
                        label="Ошибка"
                        status="error"
                        iconLeft={IconWarning}
                    />
                </div>
            );
        }

        const pathValue = R.path(R.split('.', dotNotationField), data);
        const fieldValue = data[field];
        let value: string | undefined = '';

        if (pathValue) {
            value = formatValue?.(pathValue);
        } else if (fieldValue) {
            value = formatValue?.(fieldValue);
        }

        return LeftSide ? (
            <span>
                {LeftSide} {value}
            </span>
        ) : (
            value
        );
    };

export const getBimWorkOrderRowClass: RowClassRules = {
    groupTitleClass: ({ data }: { data: AnyObject }) =>
        data?.isGroupTitle || data?.value?.isGroupTitle || data?.planValue?.isGroupTitle,
    topPinnedRowClass: ({ data }: { data: AnyObject }) => data?.topPinnedRow,
    bottomPinnedRowClass: ({ node }) => node.rowPinned === 'bottom',
};
